// Nombres del contrato y de su firma, y lo que el indicador de Personal dice de la firma (P15).
//
// Una sola fuente para todas las pantallas del módulo de Contratos: el indicador de Personal y
// la tarjeta del perfil (S02 R13, R17), el renglón del contrato en el explorador (S07) y el
// seguimiento de firma (S08). Los nombres son las etiquetas que ya usa el backend en el reporte
// de S09 (eClock6, Exceles/ContratosReporte.cs, Etiqueta(eEstadoContrato) y
// Etiqueta(eEstadoFirma)); si cambian allá, cambian aquí.
//
// Los enums llegan del API como cadena ("EnFirma", "FirmaParcial"…). Un valor que esta versión
// de la interfaz no conoce (p. ej. un "Rechazado" futuro, arquitectura §3.9) se muestra con su
// nombre tal como llega, sin romper la vista (S08 R22).
//
// Lo crea P15-I20 (el indicador de S02), el primer PR de frontend que lo necesita. P15-I19 agrega
// las opciones del filtro «Firma» del bloque «Documentos» de Personal (S08 R20). P15-I63 agrega
// abajo las reglas que sólo usa el explorador (sección «El contrato en el explorador»):
//   - el nombre y tono del explorador por estadoFirma + estado + origen (S08 R30, con «Error al
//     enviar» y el «Sin enviar» que sólo sale en los de lote): FirmaExplorador;
//   - la línea «Enviado el … · Última consulta: …» del renglón (S08 R21, D11.32): LineaFirma;
//   - PuedeActualizar / PuedeCancelar y sus permisos por perfil (S08 R31, R32; S10 R1);
//   - MensajeReconciliar / MensajeCancelar y los textos de error de cada acción (S08 R31–R33);
//   - los nombres de evento y de origen del historial (S08 R30, R33).
// Los tonos de S08 R30 son sólo del explorador y del historial: el color del indicador de
// Personal lo fija S02 R13 (src/Vistas/Personal/indicadorContrato.js).

import { fechaHoraCDMX } from "@/util/contratos-vigencia";
import { PuedeEditarContratos, PuedeBorrarContratos } from "@/util/permisos-contratos";

// Etiqueta(eEstadoFirma), S08 R30.
export const NOMBRES_ESTADO_FIRMA = Object.freeze({
  SinEnviar: "Sin enviar",
  Enviado: "Enviado",
  FirmaParcial: "Firma parcial",
  Firmado: "Firmado",
  Cancelado: "Cancelado",
  Error: "Error",
});

// Etiqueta(eEstadoContrato).
export const NOMBRES_ESTADO_CONTRATO = Object.freeze({
  Borrador: "Borrador",
  Generado: "Generado",
  EnFirma: "En firma",
  Firmado: "Firmado",
  Cancelado: "Cancelado",
  Error: "Error",
});

const Tiene = (Mapa, Valor) =>
  typeof Valor === "string" && Object.prototype.hasOwnProperty.call(Mapa, Valor);

export function EsEstadoFirmaConocido(Valor) {
  return Tiene(NOMBRES_ESTADO_FIRMA, Valor);
}

export function EsEstadoContratoConocido(Valor) {
  return Tiene(NOMBRES_ESTADO_CONTRATO, Valor);
}

// El nombre de R30, o el valor tal como llega si no se conoce (R22).
export function NombreEstadoFirma(Valor) {
  if (EsEstadoFirmaConocido(Valor)) return NOMBRES_ESTADO_FIRMA[Valor];
  return Valor == null ? "" : String(Valor);
}

export function NombreEstadoContrato(Valor) {
  if (EsEstadoContratoConocido(Valor)) return NOMBRES_ESTADO_CONTRATO[Valor];
  return Valor == null ? "" : String(Valor);
}

/**
 * Qué dice de la firma el indicador de Personal (S08 R34).
 *
 * El `ContratoResumen` del listado no trae el origen ni el avance, y la mayoría de los contratos
 * actuales vienen de la carga inicial y nunca salieron a firma; por eso `SinEnviar` no dice nada.
 * Dónde va cada cosa (tooltip, marca ✓, nombre en gris dentro del indicador) lo decide S02 R13.
 *
 * @returns {null | { nombre: string, firmado: boolean, desconocido: boolean }}
 *   `null` cuando no se dice nada de la firma (`SinEnviar`, o sin valor).
 */
export function FirmaEnIndicador(EstadoFirma) {
  if (EstadoFirma == null || EstadoFirma === "" || EstadoFirma === "SinEnviar")
    return null;
  const Conocido = EsEstadoFirmaConocido(EstadoFirma);
  return {
    nombre: NombreEstadoFirma(EstadoFirma),
    firmado: EstadoFirma === "Firmado",
    desconocido: !Conocido,
  };
}

/**
 * Las opciones del filtro «Firma» del bloque «Documentos» de Personal (S08 R20; el bloque y el
 * tooltip que lo explica son de S02 R11). «Cualquiera» no es una opción de aquí: no manda
 * `EstadosFirma`. Cada opción manda sus estados de `eEstadoFirma` separados por comas, y el
 * backend busca en **cualquier** contrato no borrado del colaborador, no sólo en el actual
 * (D09.11); con fechas de contrato, el mismo contrato debe cruzar el rango (S02 R5).
 */
export const FILTRO_FIRMA = Object.freeze([
  Object.freeze({ valor: "PorFirmar", nombre: "Por firmar", estados: Object.freeze(["Enviado", "FirmaParcial"]) }),
  Object.freeze({ valor: "Firmado", nombre: NOMBRES_ESTADO_FIRMA.Firmado, estados: Object.freeze(["Firmado"]) }),
]);

// El valor de `EstadosFirma` para una opción de FILTRO_FIRMA, o null si no es una de ellas
// («Cualquiera» o un valor que esta versión no ofrece: no se manda nada).
export function EstadosFirmaDeFiltro(Valor) {
  const Opcion = FILTRO_FIRMA.find((O) => O.valor === Valor);
  return Opcion ? Opcion.estados.join(",") : null;
}

// ---------------------------------------------------------------------------------------------------
// El contrato en el explorador (S08 R21, R22, R30–R33; P15-I63)
// ---------------------------------------------------------------------------------------------------

// Etiqueta(eEventoFirma) y Etiqueta(eOrigenEvento) del historial (R30).
export const NOMBRES_EVENTO_FIRMA = Object.freeze({
  Generado: "Generado",
  Enviado: "Enviado a firma",
  FirmaRegistrada: "Firma registrada",
  DocumentoFirmado: "Documento firmado",
  SobreCompletado: "Sobre completado",
  Cancelado: "Cancelado",
  Error: "Error",
  Consulta: "Consulta sin cambio",
});

export const NOMBRES_ORIGEN_EVENTO = Object.freeze({
  Envio: "Envío",
  Consulta: "Consulta a Legalario",
  Webhook: "Aviso de Legalario",
  Manual: "Usuario",
});

/** El nombre de un evento del historial y si se conoce (R22: uno desconocido, tal como llega y en gris). */
export function NombreEventoFirma(Valor) {
  const Conocido = Tiene(NOMBRES_EVENTO_FIRMA, Valor);
  return { nombre: Conocido ? NOMBRES_EVENTO_FIRMA[Valor] : Valor == null ? "" : String(Valor), conocido: Conocido };
}

export function NombreOrigenEvento(Valor) {
  if (Tiene(NOMBRES_ORIGEN_EVENTO, Valor)) return NOMBRES_ORIGEN_EVENTO[Valor];
  return Valor == null ? "" : String(Valor);
}

/**
 * El estado de firma de un contrato en el explorador (R30, tabla): `{ texto, tono }`, o `null` cuando no se
 * dice nada de firma (`SinEnviar` manual o de carga inicial: no se firmó en línea). `tono` ∈ "gris" | "rojo" |
 * "azul" | "verde". Un valor desconocido, su nombre en gris (R22).
 */
export function FirmaExplorador(Contrato) {
  if (!Contrato) return null;
  const F = Contrato.estadoFirma;
  if (F === "SinEnviar") {
    if (Contrato.origen === "Manual" || Contrato.origen === "Backfill") return null;
    return Contrato.estado === "Error" ? { texto: "Error al enviar", tono: "rojo" } : { texto: NOMBRES_ESTADO_FIRMA.SinEnviar, tono: "gris" };
  }
  if (F === "Enviado") return { texto: NOMBRES_ESTADO_FIRMA.Enviado, tono: "azul" };
  if (F === "FirmaParcial")
    return { texto: `${NOMBRES_ESTADO_FIRMA.FirmaParcial} · ${Contrato.avanceFirma == null ? 0 : Contrato.avanceFirma} %`, tono: "azul" };
  if (F === "Firmado") return { texto: NOMBRES_ESTADO_FIRMA.Firmado, tono: "verde" };
  if (F === "Cancelado") return { texto: NOMBRES_ESTADO_FIRMA.Cancelado, tono: "gris" };
  if (F === "Error") return { texto: NOMBRES_ESTADO_FIRMA.Error, tono: "rojo" };
  if (F == null || F === "") return null;
  return { texto: String(F), tono: "gris" };
}

/** El tono de R30 de un estado de firma suelto (el historial: «Enviado → Firmado»). */
export function TonoEstadoFirma(Valor) {
  if (Valor === "Enviado" || Valor === "FirmaParcial") return "azul";
  if (Valor === "Firmado") return "verde";
  if (Valor === "Error") return "rojo";
  return "gris";
}

/**
 * La línea del renglón con el envío, la firma y la última consulta (R21; D11.32, E73): sólo si el contrato
 * tiene `fechaEnvio` o `ultimaConsulta`; «Sin consultar todavía» sólo con `fechaEnvio` y sin
 * `ultimaConsulta`. Fechas y horas de la Ciudad de México. "" = sin línea.
 */
export function LineaFirma(Contrato) {
  if (!Contrato || (!Contrato.fechaEnvio && !Contrato.ultimaConsulta)) return "";
  const Partes = [];
  if (Contrato.fechaEnvio) Partes.push(`Enviado el ${fechaHoraCDMX(Contrato.fechaEnvio)}`);
  if (Contrato.fechaFirmado) Partes.push(`Firmado el ${fechaHoraCDMX(Contrato.fechaFirmado)}`);
  Partes.push(Contrato.ultimaConsulta ? `Última consulta: ${fechaHoraCDMX(Contrato.ultimaConsulta)}` : "Sin consultar todavía");
  return Partes.join(" · ");
}

const ARCHIVOS_FIRMADOS = ["firmado", "constancia", "certificado"];
const Archivos = (C) => (C && C.archivos) || {};

/** `Firmado` con algún archivo firmado, constancia o certificado sin resguardar (R8, R31). */
export function FaltaArchivo(Contrato) {
  return !!Contrato && Contrato.estadoFirma === "Firmado" && ARCHIVOS_FIRMADOS.some((k) => Archivos(Contrato)[k] !== true);
}

/** «Actualizar estado» se ofrece (R31): `Enviado`, `FirmaParcial`, o `Firmado` con un archivo faltante. */
export function PuedeActualizar(Contrato) {
  if (!Contrato) return false;
  return Contrato.estadoFirma === "Enviado" || Contrato.estadoFirma === "FirmaParcial" || FaltaArchivo(Contrato);
}

/** «Cancelar» se ofrece (R32, R19): `estado` ∈ Borrador, Generado, EnFirma, Error; nunca Firmado ni Cancelado. */
export function PuedeCancelar(Contrato) {
  return !!Contrato && ["Borrador", "Generado", "EnFirma", "Error"].includes(Contrato.estado);
}

/** `Contratos.Edicion` (S10 R1): SuperAdministrador (entra como Administrador), Administrador y Editor. */
export function PermisoActualizar(SuscripcionesUsuarios) {
  return PuedeEditarContratos(SuscripcionesUsuarios);
}

/** `Contratos.Borrar` (S10 R1): los mismos tres. */
export function PermisoCancelar(SuscripcionesUsuarios) {
  return PuedeBorrarContratos(SuscripcionesUsuarios);
}

const CuantosArchivos = (C) => ARCHIVOS_FIRMADOS.filter((k) => Archivos(C)[k] === true).length;

/**
 * El mensaje de «Actualizar estado» (R31), del resumen de `firmaReconciliar` y del contrato antes y después
 * (el que se volvió a pedir), en el orden de R31: `{ texto, tono }` con `tono` ∈ "success" | "warning" |
 * "info". «Estado actualizado», «Avance» y «Archivos guardados» sólo con `Consultados ≥ 1` (criterio del
 * coordinador, D11.33); «Estado actualizado» con el nombre del explorador (D11.33, E72).
 */
export function MensajeReconciliar(Antes, Despues, Resumen) {
  const A = Antes || {};
  const D = Despues || A;
  const R = Resumen || {};
  const Consultados = R.consultados || 0;
  const Errores = R.errores || 0;
  const CambioFirma = A.estadoFirma !== D.estadoFirma;
  const SubioAvance = !CambioFirma && (D.avanceFirma || 0) > (A.avanceFirma || 0);
  const MasArchivos = CuantosArchivos(D) > CuantosArchivos(A);
  const Consulto = Consultados >= 1;
  if (D.estadoFirma === "Firmado" && A.estadoFirma !== "Firmado" && Errores === 0) return { texto: "Firmado en Legalario", tono: "success" };
  if (D.estadoFirma === "Firmado" && Errores > 0) return { texto: "Firmado. Faltan archivos por guardar; se volverá a intentar", tono: "warning" };
  if (Errores > 0 && !CambioFirma && !SubioAvance && !MasArchivos)
    return { texto: "No se pudo consultar a Legalario. El contrato no cambió; se volverá a intentar", tono: "warning" };
  if (Consulto && CambioFirma) {
    const F = FirmaExplorador(D);
    return { texto: `Estado actualizado: ${F ? F.texto : NombreEstadoFirma(D.estadoFirma)}`, tono: "info" };
  }
  if (Consulto && SubioAvance) return { texto: `Avance: ${D.avanceFirma} %`, tono: "info" };
  if (Consulto && MasArchivos) return { texto: "Archivos guardados", tono: "success" };
  if (Consultados === 1) return { texto: "Sin cambios en Legalario", tono: "info" };
  if (FaltaArchivo(A) && Consultados === 0 && Errores === 0)
    return { texto: "Un archivo se borró del expediente y no se vuelve a crear", tono: "warning" };
  return { texto: "No había nada que consultar", tono: "info" };
}

export const ACTUALIZAR_FALLO = "No se pudo actualizar el estado; intenta de nuevo";
// «Actualizar estado» respondió con `Consultados ≥ 1` pero no se pudo volver a pedir el contrato (provisional,
// revisión del PR 780): no se inventa un «Sin cambios».
export const ESTADO_CONSULTADO = "Estado consultado; recarga para verlo";
export const CANCELAR_GENERICO = "No se pudo completar; revisa el estado e intenta de nuevo";

/**
 * El texto de un error de `firmaReconciliar` (R31; D11.35): red, `404 "SuscripcionID"`, `5xx`, y un `400` o un
 * `409` (que hoy no se alcanzan) dan el de R31; un `401` y lo demás (`403`, otro `404`…), `null`: el texto de
 * `base.js`. `Status` 0 = sin respuesta.
 */
export function MensajeReconciliarError(Status, Palabra) {
  if (Status === 401) return null;
  if (!Status || Status >= 500 || Status === 400 || Status === 409) return ACTUALIZAR_FALLO;
  if (Status === 404 && Palabra === "SuscripcionID") return ACTUALIZAR_FALLO;
  return null;
}

/**
 * Lo que dice la pantalla ante cada respuesta de `contratoCancelar` que no es `200` (R32), y qué se vuelve a
 * pedir: `{ texto, pide }` con `pide` ∈ "contrato" | "carpeta" | null, o `null` si R32 no lo lista (un `401`,
 * un `403`, otro `404`…): el texto de `base.js`, y se vuelve a pedir el contrato. Los dos `409 "Firmado"` se
 * deciden con el contrato que se volvió a pedir (`Releido`). Un `409` o un `400` que la tabla no lista, el
 * texto genérico de la acción (D11.35). `Status` 0 = sin respuesta.
 */
export function MensajeCancelar(Status, Palabra, Releido, Contrato) {
  if (Status === 401) return null;
  if (Status === 409 && Palabra === "Firmado")
    return Releido && Releido.estadoFirma === "Firmado"
      ? { texto: "Ya está firmado en Legalario; no se puede cancelar", pide: "contrato" }
      : { texto: "Legalario lo reporta firmado, pero el contrato no se pudo actualizar. Avisa al administrador para conciliarlo", pide: "contrato" };
  if (Status === 409 && Palabra === "ProveedorNoConfigurado") {
    const Cuenta = (Contrato && Contrato.proveedor) || "Legalario";
    return { texto: `No se puede cancelar desde aquí: la cuenta de Legalario con la que se envió (${Cuenta}) no está configurada en este ambiente`, pide: null };
  }
  if (Status === 409 && Palabra === "CanceladoEnProveedor")
    return { texto: "El documento ya se borró en Legalario, pero el contrato no quedó cancelado. Vuelve a intentarlo", pide: "contrato" };
  if (Status === 409 && Palabra === "Concurrencia") return { texto: "El contrato cambió mientras se cancelaba. Vuelve a intentarlo", pide: "contrato" };
  if (Status === 502 && Palabra === "Proveedor") return { texto: "Legalario no respondió. El contrato no cambió; intenta más tarde", pide: null };
  if (Status === 500 && Palabra === "Cifrado") return { texto: "No se pudo leer la credencial de Legalario. Avisa al administrador", pide: null };
  if (Status === 404 && Palabra === "ContratoID") return { texto: "El contrato ya no existe o no está a tu cargo", pide: "carpeta" };
  if (Status === 404 && Palabra === "SuscripcionID") return { texto: "No se pudo completar; intenta de nuevo", pide: "contrato" };
  if (!Status || Status >= 500 || Status === 409 || Status === 400) return { texto: CANCELAR_GENERICO, pide: "contrato" };
  return null;
}

/** El texto de la confirmación de «Cancelar» (R32). */
export function TextoConfirmarCancelar(Contrato) {
  const Etiqueta = (Contrato && Contrato.etiqueta) || `Contrato ${(Contrato && Contrato.consecutivo) || ""}`.trim();
  return `¿Cancelar ${Etiqueta}? Si el contrato está en Legalario, se borra allá de forma definitiva y el envío no se recupera. El contrato queda Cancelado en el expediente; no se borra.`;
}

export const HISTORIAL_FALLO = "No se pudo cargar el historial";
export const HISTORIAL_NO_DISPONIBLE = "El contrato ya no está disponible";

/** El texto de un error de `contratoFirmaEventos` (R33); `null` para un `401` (lo atiende `base.js`). */
export function MensajeHistorialError(Status, Palabra) {
  if (Status === 401) return null;
  if (Status === 404 && Palabra === "ContratoID") return HISTORIAL_NO_DISPONIBLE;
  return HISTORIAL_FALLO;
}

export default {
  NOMBRES_ESTADO_FIRMA,
  NOMBRES_ESTADO_CONTRATO,
  EsEstadoFirmaConocido,
  EsEstadoContratoConocido,
  NombreEstadoFirma,
  NombreEstadoContrato,
  FirmaEnIndicador,
  FILTRO_FIRMA,
  EstadosFirmaDeFiltro,
  NOMBRES_EVENTO_FIRMA,
  NOMBRES_ORIGEN_EVENTO,
  NombreEventoFirma,
  NombreOrigenEvento,
  FirmaExplorador,
  TonoEstadoFirma,
  LineaFirma,
  FaltaArchivo,
  PuedeActualizar,
  PuedeCancelar,
  PermisoActualizar,
  PermisoCancelar,
  MensajeReconciliar,
  MensajeReconciliarError,
  MensajeCancelar,
  TextoConfirmarCancelar,
  MensajeHistorialError,
};
